import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Swiper from 'react-id-swiper';
import parse from 'html-react-parser';
import Heading from 'js/components/Common/Heading/Heading';

import useFetchData from 'js/hooks/useFetchData/useFetchData';
import Container from 'js/components/Common/Container/Container';
import DataValidator from 'js/components/Common/DataValidator/DataValidator';
import HomeSliderService from 'js/components/Common/HomeSliderService/HomeSliderService';
import EntitySliderCard, {EntityType} from 'js/components/Common/HomeSliderService/EntitySliderCard/EntitySliderCard';

import fetchData from './fetchData';
import classes from './LastContentSlider.module.pcss';


interface Props {
    pageId: number
}

const LastContentSlider = ({pageId}: Props) => {
    const nodeRef = useRef(null);
    const [slider, setSlider] = useState(null);
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [contentItems, setContentItems] = useState<Array<EntityType>>(null);
    const [wrapOffset, setWrapOffset] = useState<string>('');

    const {isLoading, error} = useFetchData({
        query: `${fetchData(pageId)}`,
        condition: !!pageId,
        updateData: (data: any): void => {
            const pageResult = data?.page?.PostLoginPage;
            setTitle(pageResult?.interestsSliderTitle);
            setDescription(pageResult?.interestsSliderDescription);
            setContentItems(data?.latestContentByInterests);
        }
    });

    const updateMarginLeft = useCallback(() => {
        const parent:any = nodeRef?.current;
        if (!parent) return;
        const exploreWrap = parent?.childNodes[0];
        if (!exploreWrap) return null;

        const computedStyle = window.getComputedStyle(exploreWrap);

        setWrapOffset(computedStyle?.marginLeft);
    }, []);

    useEffect(() => {
        updateMarginLeft();
    }, [updateMarginLeft, contentItems]);

    useEffect(() => {
        window.addEventListener('resize', updateMarginLeft);
        return () => {
            window.removeEventListener('resize', updateMarginLeft);
        };
    }, [updateMarginLeft]);

    useEffect(() => {
        if (!slider) {
            return;
        }
        const paginationBullets = slider?.pagination?.bullets;
        if (!paginationBullets) {
            return;
        }
        HomeSliderService.setSliderBulletsClickEvent(
            paginationBullets,
            null,
            'Latest content section');
    }, [slider]);

    const params = useMemo(() =>
        HomeSliderService.getParams(setSlider, classes.Slider, classes.SecondWrap, classes.Bullets, false, false), []);
    return (
        <DataValidator data={!isLoading && !error && contentItems?.length > 0}>
            <section ref={nodeRef} className={classes.LastContentSlider}>
                <Container>
                    <Heading text={title}/>
                    <p className={classes.Description}>{parse(description || '')}</p>
                </Container>
                <div className={classes.Slider}>
                    <Container additionalClass={classes.Wrap} additionalStyles={{
                        marginLeft: wrapOffset
                    }}>
                        <Swiper {...params}>
                            {contentItems?.map(slide => (
                                <div
                                    className={`${classes.LastContentSliderItem} swiper-slide`}
                                    key={slide?.id}>
                                    <EntitySliderCard
                                        data={slide}
                                        mixPanelEventData={{
                                            section: 'Latest content section',
                                            category: 'Home page'
                                        }}
                                    />
                                </div>
                            ))}
                        </Swiper>
                    </Container>
                    <Container additionalClass={classes.SecondWrap}>
                        <div className={classes.Bullets}/>
                    </Container>
                </div>
            </section>
        </DataValidator>
    );
};

export default LastContentSlider;
